/**
 * The manual aged care plan, applied to the client's profile: the position once the plan is in
 * place (owner, 3 Oct 2026). Moved out of AgedCareAnalyser's memo, unchanged, so the Action Plan can
 * fill the means-assessment forms from the same position the analyser shows, for a plan saved
 * earlier, without the page open.
 *
 * Downsizing first (it makes the cash), then the investment property sale, the reverse mortgage,
 * the annuity, gifting and funeral bonds, then the RAD funding and the debt payoff. The last two are
 * skipped when `deferToEngine` is set: with a day to sell on, the projection does them that day,
 * and this is the CURRENT position until then.
 */
import { calculateAge } from '../../utils/index.ts';
import { markAbpFundedAcquisitionAfterLiquidation } from '../../../../shared/src/calculation/abpProjectionUtils.ts';
import { applyDownsizerContributionsToAssets } from '../../../../shared/src/calculation/downsizerContributionUtils.ts';
import { unallocatedSaleProceeds } from '../../../../shared/src/calculation/saleProceedsAllocation.ts';
import { applyDebtPayoffToFactFind } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import { applyAnnuityWithDraw, buildAnnuityImpact } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { findAssetInFactFind, parseRadFundingAssetKey, resolveRadFundingEntry } from '../../utils/entityFundingUtils.ts';
import { applyInvestmentPropertySaleToFactFind } from '../../utils/entityInvestmentPropertySale.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind, FactFindAsset } from '../../../../shared/src/calculation/factFindTypes.ts';

const asFactFind = (data: DraftFactFind): FactFind => data as unknown as FactFind;

/** What a manual aged care plan is made of — the analyser's levers, as its save records them. */
export interface ManualAgedCarePlanInputs {
    // Loosely typed, as the analyser's state is: these are the saved plan's own shapes.
    scenarioLivingExpenses: any;
    overallStrategy: any;
    downsizeDetails: any;
    investmentPropertyDetails: any;
    reverseMortgageDetails: any;
    otherStrategies: any;
    annuityRecord: any;
    radFunding: Record<string, number>;
    /** The second room's deposit funding, where both of a couple enter care — sold the same way. */
    secondRadFunding?: Record<string, number>;
    cgtOverrides: any;
    debtPayoffPlan: any;
    /** A day to sell on (a sale or entry date): the RAD sale and debt payoff are left to the projection. */
    deferToEngine: boolean;
    /**
     * Someone in care NOW (8 Oct 2026): their deposit's refundable balance opens the balance sheet as
     * a `radPaid` row of theirs, with its retention standing. The move itself is the projection's
     * (facilityMovePlugin) — this is the position the run opens in.
     */
    existingStay?: { person: 'client' | 'partner'; refundToday: number; retentionDaysTaken: number; grandfathered: boolean } | null;
}

/** The debt card's two allocations as the payoff applier takes them, or null for no payoff. */
export function debtPayoffPlanFrom(debtPayments: Record<string, unknown> | undefined, debtFunding: Record<string, unknown> | undefined, repaymentBasis: unknown): any {
    const payments = Object.fromEntries(Object.entries(debtPayments || {}).filter(([, amount]) => (Number(amount) || 0) > 0));
    if (Object.keys(payments).length === 0) return null;
    const funding = Object.fromEntries(Object.entries(debtFunding || {}).filter(([, amount]) => (Number(amount) || 0) > 0));
    return { payments, funding, repaymentBasis };
}

export function simulateManualAgedCarePlan(factFind: DraftFactFind, inputs: ManualAgedCarePlanInputs): { data: any; annuityDrawnFrom: Record<string, number> } {
    const { scenarioLivingExpenses, overallStrategy, downsizeDetails, investmentPropertyDetails, reverseMortgageDetails, otherStrategies, annuityRecord, radFunding, secondRadFunding, cgtOverrides, debtPayoffPlan, existingStay } = inputs;
    const radLiquidationDeferredToEngine = inputs.deferToEngine;
    /** What the annuity drew from each liquid asset, so the funding picker can offer it back. */
    let annuityDrawnFrom: Record<string, number> = {};
    // Loosely typed on purpose: extensive downstream mutation of data.assets/home/expenses across
    // this whole memo assumes they're always present (guaranteed by INITIAL_FACT_FIND's shape),
    // matching this function's prior JSON-clone-derived `any` typing.
    let data: any = structuredClone(factFind);
    data.expenses.livingExpenses = scenarioLivingExpenses;

    // 1. Handle downsizing first, as it generates the cash pool
    if (overallStrategy.downsizeHome) {
        /*
         * The duty on the home being bought is netted off exactly as the allocator on screen does,
         * so the two never disagree about what is left to allocate (23 Sep 2026).
         *
         * `unallocatedSaleProceeds` is handed the sale allocation and NOTHING about any strategy
         * funded out of the proceeds afterwards, so it cannot subtract one — which is how this
         * arithmetic came to be wrong in the first place (owner-caught, 25 Sep 2026). See the note
         * on that function.
         */
        const unallocatedProceeds = unallocatedSaleProceeds(downsizeDetails);

        // Remove original home
        data.home.value = 0;

        // Add new home if purchased
        if ((Number(downsizeDetails.newHomePurchasePrice) || 0) > 0) {
            data.home.value = downsizeDetails.newHomePurchasePrice;
        }
        // Add RAD Paid as an asset (if not already tracked)
            if ((Number(downsizeDetails.radAllocation) || 0) > 0) {
                if (!data.assets.radPaid) data.assets.radPaid = [];
                data.assets.radPaid = [{
                    id: 'rad-paid',
                    type: 'radPaid',
                    description: 'RAD Paid',
                    value: downsizeDetails.radAllocation,
                    ownership: 'client'
                }];
            }
        /*
         * THE PROCEEDS GO IN AT THEIR FULL VALUE, and the annuity is withdrawn from them ONCE,
         * lower down, by `applyAnnuityToFactFind`.
         *
         * OWNER-CAUGHT, 25 September 2026. This used to subtract the annuity's purchase price here
         * "so we don't double-count" — but the applier then withdrew the SAME amount from the SAME
         * account, and a third deduction followed as a "safety net". On the owner's plan
         * ($194,713 of unallocated proceeds, a $190,000 annuity) this account was created holding
         * $4,713; the applier drained that and took the remaining $185,287 from the first bank
         * account; and the picker then offered anz with $14,713 and no proceeds row at all. The
         * household gave up $380,000 of assets to buy a $190,000 annuity, which understates the
         * assets test, overstates the Age Pension and understates the means-tested care fee.
         * One withdrawal, in one place, is the only arrangement that cannot drift again.
         */
        const saleProceedsForBank = unallocatedProceeds;
        if (saleProceedsForBank > 0) {
            if (!data.assets.bankAccounts) data.assets.bankAccounts = [];
            const defaultIncomeRate = (data.assets.bankAccounts && data.assets.bankAccounts.length > 0)
                ? (data.assets.bankAccounts[0].incomeRate || 2)
                : 2;
            data.assets.bankAccounts.push({
                id: 'downsize-proceeds',
                type: 'bankAccounts',
                description: 'Unallocated Sale Proceeds',
                value: saleProceedsForBank,
                ownership: 'joint',
                incomeRate: defaultIncomeRate,
                growthRate: 0
            });
        }

        applyDownsizerContributionsToAssets(data.assets, asFactFind(factFind), downsizeDetails, {
            idPrefix: 'downsizer-contrib',
            assetId: 'downsizer-contrib',
            partnerAssetId: 'partner-downsizer-contrib',
        });

        // Add funeral bond
        if ((Number(downsizeDetails.funeralBondPurchase) || 0) > 0) {
            if (!data.assets.funeralBonds) data.assets.funeralBonds = [];
            data.assets.funeralBonds.push({
                id: 'downsize-fbond',
                type: 'funeralBonds',
                description: 'Funeral Bond from Proceeds',
                value: downsizeDetails.funeralBondPurchase,
                ownership: 'client'
            });
        }
        // Add investment property from proceeds
        if ((Number(downsizeDetails.purchaseInvestmentProperty) || 0) > 0) {
            if (!data.assets.investmentProperty) data.assets.investmentProperty = [];
            data.assets.investmentProperty.push({
                id: 'downsize-inv-prop',
                type: 'investmentProperty',
                description: 'Investment property (from downsize)',
                value: downsizeDetails.purchaseInvestmentProperty,
                ownership: 'client',
                rent: Number(downsizeDetails.investmentPropertyRent) || 0,
                expenses: Number(downsizeDetails.investmentPropertyExpenses) || 0,
                growthRate: 5,
                costBase: downsizeDetails.purchaseInvestmentProperty,
                acquisitionDate: 'post'
            });
        }
        // Add shares from proceeds
        if ((Number(downsizeDetails.purchaseShares) || 0) > 0) {
            if (!data.assets.shares) data.assets.shares = [];
            data.assets.shares.push({
                id: 'downsize-shares',
                type: 'shares',
                description: 'Shares (from downsize)',
                value: downsizeDetails.purchaseShares,
                ownership: 'client',
                incomeRate: 4,
                growthRate: 5,
                costBase: downsizeDetails.purchaseShares,
                frankingCredit: 70,
                reinvest: false
            });
        }
    }

    // 1b. Handle investment property sale (entity proceeds → entity pool when trust/company-owned)
    if (overallStrategy.sellInvestmentProperty && investmentPropertyDetails) {
        applyInvestmentPropertySaleToFactFind(data, factFind, investmentPropertyDetails);
    }

    // 2. Handle Reverse Mortgage (before RAD funding to allow RAD allocation)
    if (overallStrategy.reverseMortgage && reverseMortgageDetails) {
        const rmDetails = reverseMortgageDetails;
        const loanAmount = parseFloat(String(rmDetails.loanAmount)) || 0;
        const establishmentFees = parseFloat(String(rmDetails.establishmentFees)) || 0;
        const netProceeds = Math.max(0, loanAmount - establishmentFees);
        
        // Handle lump sum - adds funds immediately
        if (rmDetails.loanStructure === 'lump_sum' && netProceeds > 0) {
            // If useForRad is checked, allocate to RAD (handled in RAD funding section)
            // Otherwise, add to bank account
            if (!rmDetails.useForRad) {
                if (!data.assets.bankAccounts) data.assets.bankAccounts = [];
                // Find first bank account or create new one
                if (data.assets.bankAccounts.length > 0) {
                    const firstBankAccount = data.assets.bankAccounts[0];
                    firstBankAccount.value = parseFloat(String(firstBankAccount.value)) + netProceeds;
                } else {
                    data.assets.bankAccounts.push({
                        id: 'reverse-mortgage-proceeds',
                        type: 'bankAccounts',
                        description: 'Reverse Mortgage Proceeds',
                        value: netProceeds,
                        ownership: 'client',
                        // Interest, not capital growth — see bankPlugin.ts's DEFAULT_BANK_GROWTH_RATE_PERCENT.
                        // `earningsRate` is read by nothing; `incomeRate` is what incomeAssessment.ts assesses.
                        incomeRate: 2,
                        growthRate: 0
                    });
                }
            }
        }
        // Note: Regular payments are handled in projections (they accumulate over time)
    }

    // 2.5. Handle Other Strategies (gifting, funeral bonds, annuity) - BEFORE RAD funding
    // This ensures assets are deducted before RAD allocation
    
    // Apply annuity strategy if selected
    if (otherStrategies.annuity && annuityRecord) {
        // Only apply if purchase amount is set
        if (!annuityRecord.purchaseAmount || Number(annuityRecord.purchaseAmount) <= 0) {
        } else {
            // Build annuity impact first to get calculated values (including residualValue)
            try {
                // Map funding source from assetId format to type format for buildAnnuityImpact
                const mappedAnnuityRecord = { ...annuityRecord };
                
                // Ensure required fields are present
                if (!mappedAnnuityRecord.age) {
                    if (factFind?.client?.dob) {
                        mappedAnnuityRecord.age = calculateAge(factFind.client?.dob);
                    }
                }
                
                // Set partnerAge if owner is joint OR if reversionary is checked (reversionary requires partner age for joint-life calculation)
                // This affects the income rate calculation (joint discount applies when partnerAge is set)
                if ((mappedAnnuityRecord.owner === 'joint' || mappedAnnuityRecord.reversionary) && !mappedAnnuityRecord.partnerAge && factFind?.partner?.dob) {
                    mappedAnnuityRecord.partnerAge = calculateAge(factFind.partner.dob);
                }
                
                // Ensure age is valid before proceeding - if not, skip annuity application
                if (mappedAnnuityRecord.age && mappedAnnuityRecord.age > 0) {
                    // Ensure we have the ID from annuityRecord so we can replace the existing annuity
                    if (annuityRecord.id) {
                        mappedAnnuityRecord.id = annuityRecord.id;
                    }
                    if (annuityRecord.fundingSource && typeof annuityRecord.fundingSource === 'object') {
                    // If fundingSource is an object with id like "bankAccounts-123", extract the type
                    const sourceId = annuityRecord.fundingSource.id || '';
                    if (sourceId.includes('-')) {
                        const parts = sourceId.split('-');
                        const assetType = parts[0];
                        // Map to expected type format
                        if (assetType === 'bankAccounts') {
                            mappedAnnuityRecord.fundingSource = {
                                ...annuityRecord.fundingSource,
                                type: 'bank'
                            };
                        } else if (assetType === 'accountBasedPensions') {
                            mappedAnnuityRecord.fundingSource = {
                                ...annuityRecord.fundingSource,
                                type: 'abp'
                            };
                        } else if (assetType === 'superannuation') {
                            mappedAnnuityRecord.fundingSource = {
                                ...annuityRecord.fundingSource,
                                type: 'super'
                            };
                        } else {
                            mappedAnnuityRecord.fundingSource = {
                                ...annuityRecord.fundingSource,
                                type: assetType
                            };
                        }
                    }
                }
                
                const annuityRecordForCalculation = buildAnnuityImpact(data, mappedAnnuityRecord);
                if (annuityRecordForCalculation && !annuityRecordForCalculation.warnings) {
                    // Ensure fundingSource is properly mapped for applyAnnuityToFactFind
                    if (annuityRecordForCalculation.fundingSource && typeof annuityRecordForCalculation.fundingSource === 'object') {
                        const sourceId = annuityRecordForCalculation.fundingSource.id || '';
                        if (sourceId.includes('-')) {
                            const parts = sourceId.split('-');
                            const assetType = parts[0];
                            const assetId = parts.slice(1).join('-');
                            if (assetType === 'bankAccounts') {
                                annuityRecordForCalculation.fundingSourceType = 'bank';
                            } else if (assetType === 'accountBasedPensions') {
                                annuityRecordForCalculation.fundingSourceType = 'abp';
                            } else if (assetType === 'superannuation') {
                                annuityRecordForCalculation.fundingSourceType = 'super';
                            } else {
                                annuityRecordForCalculation.fundingSourceType = assetType;
                            }
                            annuityRecordForCalculation.fundingSourceId = assetId;
                        }
                    }
                    /*
                     * ONE WITHDRAWAL, AND A RECORD OF WHERE IT CAME FROM.
                     *
                     * `applyAnnuityToFactFind` is the single place the purchase leaves the assets.
                     * The "safety net" that used to follow it took the same amount a second time
                     * from the proceeds account — see the note where those proceeds are added.
                     *
                     * What each asset gave up is measured here, either side of the call, and
                     * carried out of this memo so the funding picker can offer it back. The
                     * picker answers "what could this annuity be bought with", and the money the
                     * annuity is already using belongs in that answer; without it the list shrank
                     * as the client typed (owner-caught, 25 September 2026).
                     */
                    const annuityApplied = applyAnnuityWithDraw(data, annuityRecordForCalculation);
                    data = annuityApplied.factFind;
                    annuityDrawnFrom = annuityApplied.drawnFrom;

                    const newAnnuity = data.assets?.annuities?.find((a: any) => a.id === annuityRecordForCalculation.id);
                    
                    // CRITICAL: Verify the annuity was added correctly
                    if (!newAnnuity) {
                        console.error('ERROR: Annuity was not found in assets after applyAnnuityToFactFind!');
                        console.error('All annuities in data:', data.assets?.annuities);
                    } else if (newAnnuity.annualPayment !== annuityRecordForCalculation.annualPayment) {
                        console.error('ERROR: Annuity annualPayment mismatch!', {
                            expected: annuityRecordForCalculation.annualPayment,
                            actual: newAnnuity.annualPayment
                        });
                    }
                    } else {
                        // Fallback to raw record if build fails or returns warnings
                    }
                } else {
                }
            } catch (error) {
                console.error('Error building annuity impact for factFind application:', error);
                // Don't apply annuity if there's an error - let user fix the configuration first
            }
        }
    }
    
    // Apply gifting strategy if selected
    if (otherStrategies.gifting && (Number(otherStrategies.giftingAmount) || 0) > 0) {
        const giftingAmount = parseFloat(String(otherStrategies.giftingAmount)) || 0;
        const sourceIdentifier = otherStrategies.giftingSource || 'bankAccounts';
        
        // Parse assetId format: "type-assetId" or legacy "type"
        let sourceType, targetAssetId;
        if (sourceIdentifier.includes('-')) {
            const parts = sourceIdentifier.split('-');
            sourceType = parts[0];
            targetAssetId = parts.slice(1).join('-'); // Rejoin in case asset.id contains dashes
        } else {
            sourceType = sourceIdentifier;
            targetAssetId = null; // Will deduct from all assets of this type
        }
        
        // Handle legacy 'bank' mapping
        if (sourceType === 'bank') sourceType = 'bankAccounts';
        
        // Deduct from source asset(s)
        if (data.assets[sourceType] && data.assets[sourceType].length > 0) {
            let remaining = giftingAmount;
            
            // If specific asset ID provided, find and deduct from that asset only
            if (targetAssetId) {
                const assetIndex = data.assets[sourceType].findIndex((asset: any) => asset.id === targetAssetId);
                if (assetIndex >= 0) {
                    const asset = data.assets[sourceType][assetIndex];
                    const currentValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                    const deduction = Math.min(remaining, currentValue);
                    // Update value (handle both value and balance properties)
                    if (asset.balance !== undefined) {
                        asset.balance = Math.max(0, currentValue - deduction);
                    } else {
                        asset.value = Math.max(0, currentValue - deduction);
                    }
                    remaining -= deduction;

                    // Remove asset if fully depleted
                    const newValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                    if (newValue <= 0) {
                        data.assets[sourceType].splice(assetIndex, 1);
                    }
                }
            } else {
                // Legacy: deduct from all assets of this type
                for (let i = 0; i < data.assets[sourceType].length && remaining > 0; i++) {
                    const asset = data.assets[sourceType][i];
                    const currentValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                    const deduction = Math.min(remaining, currentValue);
                    // Update value (handle both value and balance properties)
                    if (asset.balance !== undefined) {
                        asset.balance = Math.max(0, currentValue - deduction);
                    } else {
                        asset.value = Math.max(0, currentValue - deduction);
                    }
                    remaining -= deduction;

                    // Remove asset if fully depleted
                    const newValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                    if (newValue <= 0) {
                        data.assets[sourceType].splice(i, 1);
                        i--; // Adjust index after removal
                    }
                }
            }
        }
    }
    
    // Apply funeral bonds strategy if selected
    if (otherStrategies.funeralBonds && (Number(otherStrategies.funeralBondAmount) || 0) > 0) {
        const funeralBondAmount = parseFloat(String(otherStrategies.funeralBondAmount)) || 0;
        const sourceIdentifier = otherStrategies.funeralBondSource || 'bankAccounts';
        
        // Parse assetId format: "type-assetId" or legacy "type"
        let sourceType, targetAssetId;
        if (sourceIdentifier.includes('-')) {
            const parts = sourceIdentifier.split('-');
            sourceType = parts[0];
            targetAssetId = parts.slice(1).join('-'); // Rejoin in case asset.id contains dashes
        } else {
            sourceType = sourceIdentifier;
            targetAssetId = null; // Will deduct from all assets of this type
        }
        
        // Handle legacy 'bank' mapping
        if (sourceType === 'bank') sourceType = 'bankAccounts';
        
        // Deduct from source asset(s)
        if (data.assets[sourceType] && data.assets[sourceType].length > 0) {
            let remaining = funeralBondAmount;
            
            // If specific asset ID provided, find and deduct from that asset only
            if (targetAssetId) {
                const assetIndex = data.assets[sourceType].findIndex((asset: any) => asset.id === targetAssetId);
                if (assetIndex >= 0) {
                    const asset = data.assets[sourceType][assetIndex];
                    const currentValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                    const deduction = Math.min(remaining, currentValue);
                    // Update value (handle both value and balance properties)
                    if (asset.balance !== undefined) {
                        asset.balance = Math.max(0, currentValue - deduction);
                    } else {
                        asset.value = Math.max(0, currentValue - deduction);
                    }
                    remaining -= deduction;

                    // Remove asset if fully depleted
                    const newValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                    if (newValue <= 0) {
                        data.assets[sourceType].splice(assetIndex, 1);
                    }
                }
            } else {
                // Legacy: deduct from all assets of this type
                for (let i = 0; i < data.assets[sourceType].length && remaining > 0; i++) {
                    const asset = data.assets[sourceType][i];
                    const currentValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                    const deduction = Math.min(remaining, currentValue);
                    // Update value (handle both value and balance properties)
                    if (asset.balance !== undefined) {
                        asset.balance = Math.max(0, currentValue - deduction);
                    } else {
                        asset.value = Math.max(0, currentValue - deduction);
                    }
                    remaining -= deduction;

                    // Remove asset if fully depleted
                    const newValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                    if (newValue <= 0) {
                        data.assets[sourceType].splice(i, 1);
                        i--; // Adjust index after removal
                    }
                }
            }
            
            // Add funeral bond asset (exempt from asset test)
            if (!data.assets.funeralBonds) data.assets.funeralBonds = [];
            data.assets.funeralBonds.push({
                id: `other-strategy-fbond-${Date.now()}`,
                type: 'funeralBonds',
                description: 'Funeral Bond (Other Strategy)',
                value: funeralBondAmount,
                ownership: 'client',
                incomeRate: 0,
                growthRate: 0
            });
        }
    }

    // 3. Handle RAD funding from existing assets AND reverse mortgage.
    //
    // Skipped whenever there is a day to sell ON — a nominated sale date, or the entry date standing in
    // for one (`effectiveRadFundingDate`). The engine then owns the liquidation and performs it that
    // day (see `radPlugin`), so doing it here as well would sell the portfolio twice. It is also the
    // honest CURRENT position — assets the client has not sold yet should not show as sold in the
    // before/after snapshot, the insight panels or the SOA, all of which read this.
    //
    // Only a blank entry date falls through to here, and there it is right: care is already under way,
    // so the sale happens at the start of the projection, which is the same thing.
    let totalRadPaid = 0;
    const radFundingDeferredToEngine = radLiquidationDeferredToEngine;
    // The second room's deposit (both of a couple entering care) is sold from the same balance
    // sheet, after the first — the `net` of it is that room's deposit, not this one's.
    const depositSales: Array<[Record<string, number>, boolean]> = [[radFunding, true], [secondRadFunding || {}, false]];
    if (!radFundingDeferredToEngine) depositSales.forEach(([bag, firstRoom]) => Object.entries(bag).forEach(([assetKey, amount]) => {
        const parsed = parseRadFundingAssetKey(assetKey);
        if (!parsed) return;
        const { gross, net } = resolveRadFundingEntry(factFind, assetKey, amount, cgtOverrides);
        if (gross <= 0) return;

        const located = findAssetInFactFind(data, parsed.assetType, parsed.assetId);
        if (!located) return;

        const originalValue = parseFloat(String(located.asset.value)) || 0;
        const merged = {
            ...located.asset,
            value: originalValue - gross,
        };
        if (parsed.assetType === 'accountBasedPensions' && gross > 0) {
            // DraftAsset is structurally compatible with FactFindAsset (see types/formDraft.ts); the
            // shared calc-engine fn defensively coerces internally regardless, so this cast is safe
            // (see factFindTypes.ts header comment).
            markAbpFundedAcquisitionAfterLiquidation(merged as unknown as FactFindAsset, data, 1);
        }
        data.assets[parsed.assetType][located.index] = merged;
        if (firstRoom) totalRadPaid += net;
    }));
    
    // Add reverse mortgage to RAD if useForRad is checked (only available for lump sum).
    // Deferred alongside the asset sales: it funds the same deposit, on the same day.
    if (!radFundingDeferredToEngine && overallStrategy.reverseMortgage && reverseMortgageDetails?.useForRad &&
        reverseMortgageDetails.loanStructure === 'lump_sum') {
        const rmLoanAmount = parseFloat(String(reverseMortgageDetails.loanAmount)) || 0;
        const rmEstablishmentFees = parseFloat(String(reverseMortgageDetails.establishmentFees)) || 0;
        const rmNetProceeds = Math.max(0, rmLoanAmount - rmEstablishmentFees);
        totalRadPaid += rmNetProceeds;
    }
    
    // 3b. Debt payoff, on exactly the same contract as the RAD sale above: skipped whenever there is a
    // day to do it on, because `debtPayoffPlugin` then owns the event and doing it here as well would
    // clear the debt twice and sell the funding assets twice. Both branches read the same
    // `effectiveRadFundingDate`, so they cannot disagree.
    //
    // When it DOES apply here, it matters: the Financial Impact panel reads this fact-find for its
    // normalised year, and a plan that retires a debt must not show the client still servicing it.
    if (!radFundingDeferredToEngine && debtPayoffPlan) {
        data = applyDebtPayoffToFactFind(data, debtPayoffPlan, cgtOverrides).factFind;
    }

    // 3b. Someone in care now: the deposit they hold, as the run opens.
    if (existingStay && existingStay.refundToday > 0) {
        if (!data.assets.radPaid) data.assets.radPaid = [];
        const already = (data.assets.radPaid as Array<{ ownership?: string }>).some((r) => (r.ownership ?? 'client') === existingStay.person);
        if (!already) {
            data.assets.radPaid.push({
                id: 'radPaid',
                type: 'radPaid',
                description: 'RAD Paid',
                value: existingStay.refundToday,
                income: 0,
                growthRate: 0,
                ownership: existingStay.person,
                __radRetentionDaysTaken: existingStay.retentionDaysTaken,
                ...(existingStay.grandfathered ? { __noRetention: true } : {}),
            });
        }
    }

    // 4. Add RAD asset if any RAD was paid
    if (totalRadPaid > 0) {
        if (!data.assets.radPaid) data.assets.radPaid = [];
        // Check if RAD asset already exists (from downsize or investment property)
        const existingRadIndex = data.assets.radPaid.findIndex((a: any) => a.id === 'rad-deposit');
        if (existingRadIndex > -1) {
            // Update existing RAD asset
            const existingRadValue = parseFloat(String(data.assets.radPaid[existingRadIndex].value)) || 0;
            data.assets.radPaid[existingRadIndex].value = existingRadValue + totalRadPaid;
        } else {
            // Create new RAD asset
            data.assets.radPaid.push({
                id: 'rad-deposit',
                type: 'radPaid',
                description: 'RAD Deposit',
                value: totalRadPaid,
                ownership: 'client',
                growthRate: 0 // RAD doesn't grow, but may have retention
            });
        }
    } else {
    }

    return { data, annuityDrawnFrom };
}
